import React, { useContext } from "react";
import NumberFormat from "react-number-format";
import { PaymentFrequency } from "../../../interfaces/enums/PaymentFrequency";
import { Severity } from "../../../interfaces/enums/Severity";
import Alert from "../../Alert/Alert";
import ProviderAddons from "../../ProviderAddOn/ProviderAddons/ProviderAddons";
import Cashback from "../../Cashback/Cashback";
import QuoteBreakdownSummaryRow from "../QuoteBreakdownSummaryRow";
import { QuoteGroupContext } from "../../../../compare/providers/RootContextProvider";

//styles
import cx from "classnames";


interface IQuoteBreakdownSummaryFooterProps {
    totalDiscount: number;
    paymentFrequency: PaymentFrequency;
    providerId: number;
    totalPremium: number;
    displayError: string;
    totalCashback: number;
}

const QuoteBreakdownSummaryFooter: React.FC<IQuoteBreakdownSummaryFooterProps> = (
    props
) => {
    const {
        totalDiscount,
        paymentFrequency,
        providerId,
        totalPremium,
        displayError,
        totalCashback
    } = props;

    const { selectors } = useContext(QuoteGroupContext);
    const providerAddOns = selectors.getProviderAddOns();
    const selectedAddOns = selectors.getSelectedAddOns(providerId);
    const quoteResult = selectors.getQuoteCardResult(providerId);

    return (
        <div className="gap-xl">
            <div className="gap-l padding-horizontal-l">
                {quoteResult.result.policyFee > 0 && (
                    <QuoteBreakdownSummaryRow
                        title="Policy Fee"
                        className="text-font-size-s"
                        tip="Your insurer charges this fee for the administration of your policy and may change the fee at any time.">
                        <NumberFormat
                            className={cx({
                                ["text-strikethrough"]: quoteResult.result.policyFeeDiscount > 0
                            })}
                            value={quoteResult.result.policyFee + quoteResult.result.policyFeeDiscount}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator={true}
                            isNumericString={true}
                            decimalScale={2}
                            fixedDecimalScale
                        />
                    </QuoteBreakdownSummaryRow>
                )}
                {quoteResult.result.policyFeeDiscount > 0 && (
                    <QuoteBreakdownSummaryRow
                        title={`After Discount (${Math.round(quoteResult.result.policyFeeDiscount / (quoteResult.result.policyFee + quoteResult.result.policyFeeDiscount)*100)}%)`}
                        className="text-font-size-s gap-l">
                        <NumberFormat
                            value={quoteResult.result.policyFee}
                            prefix={"$"}
                            displayType={"text"}
                            thousandSeparator={true}
                            isNumericString={true}
                            decimalScale={2}
                            fixedDecimalScale
                        />
                    </QuoteBreakdownSummaryRow>
                )}
                {selectedAddOns.map((s) => {
                    const providerAddOn = providerAddOns.find((p) => p.systemName === s.providerAddOnSysName);
                    if (!providerAddOn || s.discountAmount <= 0) return;

                    const formattedPercentage = providerAddOn.discountPercentage
                        ? `${providerAddOn.discountPercentage}% ` : "";

                    const title = `${formattedPercentage} ${providerAddOn.addOnName} discount`;

                    return (
                        <QuoteBreakdownSummaryRow key={providerAddOn.addOnName} title={title} tip={providerAddOn.tipText}>
                            <NumberFormat
                                value={s.discountAmount}
                                prefix={"- $"}
                                displayType="text"
                                decimalScale={2}
                                fixedDecimalScale
                                className="text-brand"
                            />
                        </QuoteBreakdownSummaryRow>
                    );

                })}
                {totalDiscount > 0 && (
                    <QuoteBreakdownSummaryRow title="Total other discounts">
                        <NumberFormat
                            value={totalDiscount}
                            prefix={"- $"}
                            displayType="text"
                            decimalScale={2}
                            fixedDecimalScale
                            className="text-brand"
                        />
                    </QuoteBreakdownSummaryRow>
                )}
                <QuoteBreakdownSummaryRow
                    title={`Total Premium (${paymentFrequency})`}
                    subtext="This is the regular premium you pay your insurer"
                    weight="bold">
                    <NumberFormat
                        value={totalPremium}
                        prefix={"$"}
                        displayType={"text"}
                        thousandSeparator={true}
                        isNumericString={true}
                        decimalScale={2}
                        fixedDecimalScale
                        className="text-bold"
                    />
                </QuoteBreakdownSummaryRow>
            </div>

            {selectedAddOns.map((s) => {
                const providerAddOn = providerAddOns.find((p) => p.systemName === s.providerAddOnSysName);
                if (!providerAddOn) return;

                return <ProviderAddons key={providerAddOn.addOnName} className="gap-pre-m" providerAddOn={providerAddOn} />;
            })}

            {displayError && (
                <Alert className="gap-pre-m" severity={Severity.Error}>
                    {displayError}
                </Alert>
            )}

            {totalCashback > 0 && (
                <Cashback className="gap-pre-m" cashbackAmount={totalCashback} />
            )}
        </div>
    );
};

export default QuoteBreakdownSummaryFooter;
